import { chain } from '@leprecoins/effector';
import { querySync } from 'atomic-router';
import { combine, createEvent, createStore, sample } from 'effector';
import { debounce, empty } from 'patronum';
import { createEditGameSettingModel } from 'features/edit-game-setting';
import { createResetGameSettingModel } from 'features/reset-game-setting';
import { accountTreeModel } from 'entities/account';
import { createGameSettingRegistryModel } from 'entities/game-setting';
import { createGetGamesQuery } from 'shared/api';
import { authorizedGameSettingsRoute, controls } from 'shared/routing';

const getGamesQuery = createGetGamesQuery();

const $gameId = createStore<number | null>(null);

const $games = getGamesQuery.$data.map((games) =>
	games ? games.map((game) => ({ value: `${game.id}`, label: game.title })) : [],
);

const $game = combine(
	{
		gameId: $gameId,
		games: getGamesQuery.$data,
	},
	({ gameId, games }) => games?.find((game) => game.id === gameId) ?? null,
);

const setGameId = createEvent<number | null>();

const resetGameSettingModel = createResetGameSettingModel();

const editGameSettingModel = createEditGameSettingModel();

const gameSettingsRegistryModel = createGameSettingRegistryModel({
	$gameId,
	$currencies: accountTreeModel.$currencies,
	disabled: empty($gameId),
	onUpdate: [
		resetGameSettingModel.mutation.finished.success,
		editGameSettingModel.editGameSettingsMutation.finished.success,
		$gameId,
	],
});

chain(setGameId, $gameId);

sample({
	clock: debounce(accountTreeModel.$selectedNodes, 300),
	target: gameSettingsRegistryModel.model.filters.accountIds.setValue,
});

chain(gameSettingsRegistryModel.model.clearFilters, accountTreeModel.clearSelectedNodes);

querySync({
	source: { id: $gameId },
	route: authorizedGameSettingsRoute,
	controls,
});

sample({
	clock: authorizedGameSettingsRoute.$query,
	fn: ({ id }) => Number(id) || null,
	target: $gameId,
});

chain(authorizedGameSettingsRoute.opened, getGamesQuery.start);

sample({
	clock: getGamesQuery.finished.success,
	fn: ({ result }) => result[0]?.id,
	target: $gameId,
});

export const gameSettingsPageModel = {
	gameSettingsRegistryModel,
	resetGameSettingModel,
	editGameSettingModel,
	$gameId,
	$games,
	$game,
	setGameId,
};
